import {
    Box,
    Button,
    ColumnLayout,
    Container,
    FormField,
    Header,
    Multiselect,
    Select,
    SelectProps,
    SpaceBetween,
    Textarea
} from "@amzn/awsui-components-react-v3";
import React, {useState} from "react";
import {SEARCH_FILTER} from "../LmdpConstants";
import {SearchFilteredData} from "./LmdpClusterAndSiteSerachFilterData";
import {DropdownStatusProps} from "@amzn/awsui-components-react-v3/polaris/internal/components/dropdown-status";
import Toggle from "@amzn/awsui-components-react-v3/polaris/toggle";
import { convertMultiSelectOptionsToPolaris3Schema } from "../../../util/PolarisUtils";
import { AT_DEST_HUB_FILTER, DESTINATION_HUB_ETA_SOURCE, INTERNAL_RACK_TYPES, POSITION_READY_STATUS, REGION_FILTER, SHIPMENT_STATUS } from "../../searchpage/SearchFilterConfig";

interface LoadableOptions {
    loading: boolean,
    isError: boolean,
    errorDescription?: string,
    options: SelectProps.Options
}

interface LmdpSearchFilterProps {
    loading: boolean,
    regionOptions?: LoadableOptions,
    clusterOptions: LoadableOptions,
    superAzOptions: LoadableOptions,
    siteOptions: LoadableOptions,
    performSearch: (filteredData: SearchFilteredData) => any,
    resetSearchResults: () => void,
    isClusterOrSuperAzList?: boolean,
    isToggleRequired?: boolean,
    isToggleDisabled?: boolean,
    toggleState?: boolean,
    setToggleState?: (state: boolean) => void,
    setToggleDisabled?: (isDisabled: boolean) => void,
}

const EMPTY_SEARCH_FILTERS: SearchFilteredData = {
    searchBarInput: "",
    cluster: [],
    destinationSite: [],
    superAz: [],
    region: [],
    rackType: "", // TODO -> This needs to changed to use internalRackType param down the line.
    internalRackType: [],
    positionStatuses: [],
    presentAtDestHub: null,
    shipmentStatus: [],
    destinationHubEtaSource: []
}

export default function (props: LmdpSearchFilterProps) {

    const [filteredData, setFilteredData] = useState<SearchFilteredData>(EMPTY_SEARCH_FILTERS);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [clusterSelected, setClusterSelected] = useState<boolean>(false);
    const [superAzSelected, setSuperAzSelected] = useState<boolean>(false);
    const columns = props.isClusterOrSuperAzList ? 2 : 3;
    const showAllRackTextCSS = !props.toggleState ? {fontWeight: "bold"} : {};
    const showLockedTextCSS = props.toggleState ? {fontWeight: "bold"} : {};
    const clusterSearchProps = {
        disabled: props.loading || superAzSelected,
        loadingText: SEARCH_FILTER.FILTER_CLUSTER_LOADING_TEXT,
        options: props.clusterOptions.options,
        placeholder: SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT,
        virtualScroll: true,
        filteringType: 'auto' as const,
        errorText: determineErrorText(props.clusterOptions, SEARCH_FILTER.FILTER_CLUSTER_ERROR_TEXT),
        statusType: determineLoadingType(props.clusterOptions)
    };

    const handleClusterChange = (event: any, isMultiSelect: boolean) => {
        setErrorMessage("");
        setClusterSelected(true);
        setFilteredData({
            ...filteredData,
            cluster: isMultiSelect ? event.detail.selectedOptions : [event.detail.selectedOption]
        });
    };

    return (
        <>
            <Container header={
                <Header variant="h2" actions={props.isToggleRequired ? <div style={{display: "flex", flexDirection: "row"}}>
                    {!props.isToggleDisabled ? <span style={{marginRight: "1rem", ...showAllRackTextCSS}}> Show Only Locked Racks </span> :
                        <span style={{marginRight: "1rem", color: "darkgrey", ...showAllRackTextCSS}}> Show Only Locked Racks </span>}
                    <Toggle
                        disabled={!!props.isToggleDisabled}
                        onChange={({ detail }) =>
                            props.setToggleState === undefined ? null : props.setToggleState(detail.checked)
                        }
                        checked={props.toggleState === undefined ? false : props.toggleState}>
                        <span style={showLockedTextCSS}> Show all racks </span>
                    </Toggle>
                    </div> : null}> {SEARCH_FILTER.SEARCH_HEADER} </Header>}>
                <ColumnLayout columns={1}>
                    <ColumnLayout columns={columns}>
                        {props.toggleState ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_REGION_LABEL} errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading}
                                            options={convertMultiSelectOptionsToPolaris3Schema(REGION_FILTER)}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            selectedOptions={filteredData.region}
                                            onChange={event => {
                                                setErrorMessage("");
                                                setFilteredData({
                                                    ...filteredData,
                                                    region: event.detail.selectedOptions
                                                });
                                            }}
                                            errorText={SEARCH_FILTER.FILTER_REGION_ERROR_TEXT}
                                            loadingText={SEARCH_FILTER.FILTER_REGION_LOADING_TEXT}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween></div> : null}
                        <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_CLUSTER_LABEL}
                                               errorText={errorMessage}>
                                        {props.toggleState ? (
                                            <Multiselect
                                                {...clusterSearchProps}
                                                selectedOptions={filteredData.cluster}
                                                onChange={event => handleClusterChange(event, true)}
                                            />
                                        ) : (
                                            <Select
                                                {...clusterSearchProps}
                                                selectedOption={filteredData.cluster[0]}
                                                onChange={event => handleClusterChange(event, false)}
                                            />
                                        )}
                                    </FormField>
                                </Box>
                            </SpaceBetween>
                        </div>
                        <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_SUPER_AZ_LABEL}
                                               errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading || clusterSelected}
                                            options={props.superAzOptions.options}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setSuperAzSelected(true)
                                                setFilteredData({
                                                    ...filteredData,
                                                    superAz: event.detail.selectedOptions
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            errorText={determineErrorText(props.superAzOptions, SEARCH_FILTER.FILTER_CLUSTER_ERROR_TEXT)}
                                            statusType={determineLoadingType(props.superAzOptions)}
                                            selectedOptions={filteredData.superAz}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween>
                        </div>
                        { props.isClusterOrSuperAzList ? null : <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_SITE_LABEL}>
                                        <Multiselect
                                            disabled={props.loading || (!clusterSelected && !superAzSelected)}
                                            loadingText={SEARCH_FILTER.FILTER_SITES_LOADING_TEXT}
                                            options={props.siteOptions.options}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setFilteredData({
                                                    ...filteredData,
                                                    destinationSite: event.detail.selectedOptions
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            errorText={determineErrorText(props.siteOptions, SEARCH_FILTER.FILTER_SITE_ERROR_TEXT)}
                                            selectedOptions={filteredData.destinationSite}
                                            statusType={determineLoadingType(props.siteOptions)}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween>
                        </div> }
                        {props.toggleState ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_POSITION_STATUS_LABEL} errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading}
                                            loadingText={SEARCH_FILTER.FILTER_POSITION_STATUS_LOADING_TEXT}
                                            options={convertMultiSelectOptionsToPolaris3Schema(POSITION_READY_STATUS)}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setFilteredData({
                                                    ...filteredData,
                                                    positionStatuses: event.detail.selectedOptions
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            errorText={SEARCH_FILTER.FILTER_POSITION_STATUS_ERROR_TEXT}
                                            selectedOptions={filteredData.positionStatuses}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween></div> : null}
                        {props.toggleState ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.INTERNAL_RACK_TYPE_LABEL} errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading}
                                            loadingText={SEARCH_FILTER.INTERNAL_RACK_TYPE_LOADING_TEXT}
                                            options={convertMultiSelectOptionsToPolaris3Schema(INTERNAL_RACK_TYPES)}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setFilteredData({
                                                    ...filteredData,
                                                    internalRackType: event.detail.selectedOptions
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            errorText={SEARCH_FILTER.INTERNAL_RACK_TYPE_ERROR_TEXT}
                                            selectedOptions={filteredData.internalRackType}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween></div> : null}
                        {props.toggleState ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_SHIPMENT_STATUS_LABEL} errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading}
                                            options={convertMultiSelectOptionsToPolaris3Schema(SHIPMENT_STATUS)}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            selectedOptions={filteredData.shipmentStatus}
                                            onChange={event => {
                                                setErrorMessage("");
                                                setFilteredData({
                                                    ...filteredData,
                                                    shipmentStatus: event.detail.selectedOptions
                                                });
                                            }}
                                            errorText={SEARCH_FILTER.FILTER_SHIPMENT_STATUS_ERROR_TEXT}
                                            loadingText={SEARCH_FILTER.FILTER_SHIPMENT_STATUS_LOADING_TEXT}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween></div> : null}
                        {props.toggleState ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_DESTINATION_HUB_ETA_SOURCE_LABEL} errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading}
                                            options={convertMultiSelectOptionsToPolaris3Schema(DESTINATION_HUB_ETA_SOURCE)}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            selectedOptions={filteredData.destinationHubEtaSource}
                                            onChange={event => {
                                                setErrorMessage("");
                                                setFilteredData({
                                                    ...filteredData,
                                                    destinationHubEtaSource: event.detail.selectedOptions
                                                });
                                            }}
                                            errorText={SEARCH_FILTER.FILTER_DESTINATION_HUB_ETA_SOURCE_ERROR_TEXT}
                                            loadingText={SEARCH_FILTER.FILTER_DESTINATION_HUB_ETA_SOURCE_LOADING_TEXT}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween></div> : null}
                        {props.toggleState ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_HUB_LABEL} errorText={errorMessage}>
                                        <Select
                                            disabled={props.loading}
                                            options={convertMultiSelectOptionsToPolaris3Schema(AT_DEST_HUB_FILTER)}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setFilteredData({
                                                    ...filteredData,
                                                    presentAtDestHub: event.detail.selectedOption
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            selectedOption={filteredData.presentAtDestHub}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween></div> : null}
                    </ColumnLayout>
                    <Box padding={{top: "s"}} float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button loading={props.loading} onClick={() => {
                                setFilteredData(EMPTY_SEARCH_FILTERS);
                                setClusterSelected(false);
                                setSuperAzSelected(false);
                                setErrorMessage("");
                                props.resetSearchResults();
                            }}>
                                Clear
                            </Button>
                            <Button loading={props.loading} variant="primary" onClick={() => {
                                searchableFiltersSelected(filteredData, false, true) ?
                                    props.performSearch(filteredData) :
                                    setErrorMessage(
                                        props.toggleState ? 
                                            SEARCH_FILTER.MISSING_SHOW_ALL_RACKS_SEARCH_VALUES_ERROR :
                                            SEARCH_FILTER.MISSING_SEARCH_VALUES_ERROR)
                            }}>
                                Search
                            </Button>
                        </SpaceBetween>
                    </Box>
                </ColumnLayout>
            </Container>
        </>
    );
}

export function HubViewSearchFilter (props: LmdpSearchFilterProps) {

    const [filteredData, setFilteredData] = useState<SearchFilteredData>(EMPTY_SEARCH_FILTERS);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const getNumberOfLinesInInput = () => {
        return (filteredData.rackType?.split(/\r\n|\r|\n/).length || 0);
    }

    return (
        <>
            <Container header={
                    <Header variant="h2"> {SEARCH_FILTER.SEARCH_HEADER} </Header>}>
                <ColumnLayout columns={1}>
                    <ColumnLayout columns={3}>
                        <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_RACK_TYPE_LABEL} errorText={errorMessage}>
                                        <Textarea
                                            disabled={props.loading}
                                            placeholder={SEARCH_FILTER.RACK_TYPE_PLACEHOLDER_TEXT}
                                            value={filteredData.rackType}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setFilteredData({
                                                    ...filteredData,
                                                    rackType: event.detail.value
                                                })
                                            }}
                                            // Adjusting the height of the input textArea based on the content
                                            rows={ getNumberOfLinesInInput() < 5 ? getNumberOfLinesInInput() : 4 }
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween>
                        </div>
                        { props.regionOptions ? <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_REGION_LABEL} errorText={errorMessage}>
                                        <Select
                                            disabled={props.loading}
                                            loadingText={SEARCH_FILTER.FILTER_REGION_LOADING_TEXT}
                                            options={props.regionOptions.options}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setFilteredData({
                                                    ...filteredData,
                                                    region: [event.detail.selectedOption]
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            errorText={determineErrorText(props.regionOptions, SEARCH_FILTER.FILTER_REGION_ERROR_TEXT)}
                                            selectedOption={filteredData.region[0]}
                                            statusType={determineLoadingType(props.regionOptions)}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween>
                        </div> : null }
                        <div>
                            <SpaceBetween size={"xxs"}>
                                <Box>
                                    <FormField stretch={true} label={SEARCH_FILTER.FILTER_HUB_LABEL} errorText={errorMessage}>
                                        <Multiselect
                                            disabled={props.loading}
                                            loadingText={SEARCH_FILTER.FILTER_SITES_LOADING_TEXT}
                                            options={props.siteOptions.options}
                                            placeholder={SEARCH_FILTER.SELECT_FILTER_PLACEHOLDER_TEXT}
                                            onChange={event => {
                                                setErrorMessage("")
                                                setFilteredData({
                                                    ...filteredData,
                                                    destinationSite: event.detail.selectedOptions
                                                })
                                            }}
                                            virtualScroll={true}
                                            filteringType='auto'
                                            errorText={determineErrorText(props.siteOptions, SEARCH_FILTER.FILTER_SITE_ERROR_TEXT)}
                                            selectedOptions={filteredData.destinationSite}
                                            statusType={determineLoadingType(props.siteOptions)}
                                        />
                                    </FormField>
                                </Box>
                            </SpaceBetween>
                        </div> 
                    </ColumnLayout>
                    <Box padding={{top: "s"}} float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button loading={props.loading} onClick={() => {
                                setFilteredData(EMPTY_SEARCH_FILTERS);
                                props.resetSearchResults();
                            }}>
                                Clear
                            </Button>
                            <Button loading={props.loading} variant="primary" onClick={() => {
                                searchableFiltersSelectedForHubView(filteredData) ?
                                props.performSearch(filteredData) :
                                setErrorMessage(SEARCH_FILTER.MISSING_HUB_VIEW_SEARCH_VALUES_ERROR)
                            }}>
                                Search
                            </Button>
                        </SpaceBetween>
                    </Box>
                </ColumnLayout>
            </Container>
        </>
    );
}

function getFilters(filteredData: SearchFilteredData): any {
    return Object.entries(filteredData).filter(([key, value]) => key !== "searchBarInput" && value !== null).map(([key, value]) => ({[key]: value}));
}

function searchableFiltersSelected(filteredData: SearchFilteredData, isClusterList: boolean | undefined, isSuperAzList: boolean | undefined): boolean {
    const filters = getFilters(filteredData);
    if(isClusterList === true) {
        return (filters && filters.length > 1 && filteredData.cluster !== null);
    } else if(isSuperAzList === true) {
        return filters && filters.length > 1 && (filteredData.superAz?.length > 0 || filteredData.cluster?.length > 0 || filteredData.region?.length > 0);
    } else {
        return filters && filters.length > 1 && (filteredData.destinationSite?.length > 0);
    }
}

function searchableFiltersSelectedForHubView(filteredData: SearchFilteredData): boolean {
    const filters = getFilters(filteredData);
    const isSiteSelected = filteredData.destinationSite?.length > 0;
    const isRackTypeSelected = filteredData.rackType?.length > 0;
    const isRegionSelected = filteredData.region?.length > 0;
    return filters && filters.length > 1 && (isSiteSelected || isRegionSelected || isRackTypeSelected);
}

export function determineLoadingType(siteOptions: LoadableOptions): DropdownStatusProps.StatusType {
    if (siteOptions.isError) {
        return 'error'
    }
    if (siteOptions.loading) {
        return 'loading';
    }
    return 'finished';
}

export function determineErrorText(siteOptions: LoadableOptions, defaultText: string): string {
    return siteOptions.errorDescription ? siteOptions.errorDescription : defaultText;
}